/* eslint-disable @typescript-eslint/no-explicit-any */
import { createAsyncThunk } from '@reduxjs/toolkit';
import { apiClient } from '@/utils/apiClient';

import {
  CreateTicketPayload,
  CreateTicketResponse,
  UpdateTicketDataPayload,
  AddTicketItemPayload,
  DeleteTicketItemPayload,
  SubmitDocumentPayload,
  BasicMessageResponse,
  UploadDocumentResponse,
  CompleteTicketPayload,
  CompleteTicketResponse,
  SubmitAddedItemsPayload,
  SubmitAddedItemsResponse,
} from './types';

/**
 * Read/derive the ORIGINAL backend error payload.
 * - If Axios: uses err.response.data (already parsed)
 * - If fetch-like: reads Response body (json or text)
 * - If plain string: returns the string
 * - If object: returns the object as-is
 */
/**
 * Read/derive the ORIGINAL backend error payload.
 * Works with:
 * - Axios: err.response.data
 * - fetch(Response): await resp.json()/text()
 * - Your apiClient: err.response (already parsed JSON or text)
 * - Strings / plain objects
 */
const readBackendError = async (err: any): Promise<any> => {
  // 1) Axios-style
  if (err?.response?.data !== undefined) return err.response.data;

  // 2) Your apiClient style: parsed body is directly on err.response
  if (err?.response !== undefined) {
    const resp = err.response;

    // If it's already a parsed JSON object or a raw string, return as-is
    if (typeof resp === 'string') return resp;
    if (resp && typeof resp === 'object' && !('json' in resp) && !('text' in resp)) {
      return resp; // ← THIS is the key path for your current apiClient
    }

    // 3) fetch(Response) object thrown: read it once
    try {
      if (resp && typeof resp.json === 'function') {
        try {
          const j = await resp.json();
          return j;
        } catch { /* fall through */ }
      }
      if (resp && typeof resp.text === 'function') {
        const t = await resp.text();
        try {
          return JSON.parse(t);
        } catch {
          return t;
        }
      }
    } catch { /* ignore */ }
  }

  // 4) Some clients put raw body under err.data
  if (err?.data !== undefined) return err.data;

  // 5) Plain string
  if (typeof err === 'string') {
    try { return JSON.parse(err); } catch { return err; }
  }

  // 6) Error instance → prefer attached payload if present; otherwise message
  if (err instanceof Error) {
    // if someone attached raw payload on (err as any).payload
    if ((err as any).payload !== undefined) return (err as any).payload;
    // as a last resort
    return err.message || 'Request failed';
  }

  // 7) As-is object
  if (err && typeof err === 'object') return err;

  return 'Request failed';
};

/** ───── Ticketing Thunks ─────────────────────────────────────────────
 * IMPORTANT: rejectValue is `unknown` so we can pass the server's payload verbatim
 * (object or string). Your slice should store `action.payload` directly.
 */

/** Create Ticket (POST /create/) */
export const createTicket = createAsyncThunk<
  CreateTicketResponse,
  CreateTicketPayload,
  { rejectValue: unknown }
>('ticketing/createTicket', async (payload, { rejectWithValue }) => {
  try {
    const response = await apiClient('/workflows/api/v1/ticketing/create/', {
      method: 'POST',
      body: payload,
    });
    return response as CreateTicketResponse;
  } catch (err: any) {
    return rejectWithValue(await readBackendError(err));
  }
});

/** Update Ticket Data (PATCH /update/data/) */
export const updateTicketData = createAsyncThunk<
  BasicMessageResponse,
  UpdateTicketDataPayload,
  { rejectValue: unknown }
>('ticketing/updateTicketData', async (payload, { rejectWithValue }) => {
  try {
    const response = await apiClient(
      '/workflows/api/v1/ticketing/update/data/',
      {
        method: 'PATCH',
        body: payload,
      }
    );
    return response as BasicMessageResponse;
  } catch (err: any) {
    return rejectWithValue(await readBackendError(err));
  }
});

/** Add Item to Compound (POST /add/item/) */
export const addTicketItem = createAsyncThunk<
  BasicMessageResponse,
  AddTicketItemPayload,
  { rejectValue: unknown }
>('ticketing/addTicketItem', async (payload, { rejectWithValue }) => {
  try {
    const response = await apiClient('/workflows/api/v1/ticketing/add/item/', {
      method: 'POST',
      body: payload,
    });
    return response as BasicMessageResponse;
  } catch (err: any) {
    return rejectWithValue(await readBackendError(err));
  }
});

/** Delete Item from Compound (DELETE /delete/item/) */
export const deleteTicketItem = createAsyncThunk<
  BasicMessageResponse,
  DeleteTicketItemPayload,
  { rejectValue: unknown }
>('ticketing/deleteTicketItem', async (payload, { rejectWithValue }) => {
  try {
    const response = await apiClient(
      '/workflows/api/v1/ticketing/delete/item/',
      {
        method: 'DELETE',
        body: payload,
      }
    );
    return response as BasicMessageResponse;
  } catch (err: any) {
    return rejectWithValue(await readBackendError(err));
  }
});

/** Upload Document (multipart POST /add/document/) */
export const uploadTicketDocument = createAsyncThunk<
  UploadDocumentResponse,
  SubmitDocumentPayload,
  { rejectValue: unknown }
>('ticketing/uploadTicketDocument', async (payload, { rejectWithValue }) => {
  try {
    const fd = new FormData();
    fd.append('field', payload.field);
    fd.append('file', payload.file);

    const response = await apiClient(
      '/workflows/api/v1/ticketing/add/document/',
      {
        method: 'POST',
        body: fd, // let browser set multipart boundary
      }
    );
    return response as UploadDocumentResponse;
  } catch (err: any) {
    return rejectWithValue(await readBackendError(err));
  }
});

/** Complete Ticket (POST /complete/) */
export const completeTicket = createAsyncThunk<
  CompleteTicketResponse,
  CompleteTicketPayload,
  { rejectValue: unknown }
>('ticketing/completeTicket', async (payload, { rejectWithValue }) => {
  try {
    // Ensure backend gets an iterable instead of null/undefined
    const body = {
      ...payload,
      items: payload.items ?? [],  // ALWAYS an array
    };

    const response = await apiClient('/workflows/api/v1/ticketing/complete/', {
      method: 'POST',
      body,
    });
    return response as CompleteTicketResponse;
  } catch (err: any) {
    return rejectWithValue(await readBackendError(err));
  }
});


/** Submit Added Items (batch) (POST /submit/items/) */
export const submitAddedItems = createAsyncThunk<
  SubmitAddedItemsResponse,
  SubmitAddedItemsPayload,
  { rejectValue: unknown }
>('ticketing/submitAddedItems', async (payload, { rejectWithValue }) => {
  try {
    const response = await apiClient(
      '/workflows/api/v1/ticketing/submit/items/',
      {
        method: 'POST',
        body: payload,
      }
    );
    return response as SubmitAddedItemsResponse;
  } catch (err: any) {
    return rejectWithValue(await readBackendError(err));
  }
});
